<script setup>
import {Head, Link} from '@inertiajs/vue3'
import Footer from "../../Layouts/New/Components/Footer.vue";
import ClientOnly from "@duannx/vue-client-only";
import {Modal} from "momentum-modal";
import Notifications from "../../Layouts/New/Components/Notifications.vue";
import Navigation from "../../Layouts/New/Components/Navigation.vue";
import TopicIcon from "../../Components/New/Library/TopicIcon.vue";
import CourseProgress from "../../Components/New/Courses/CourseProgress.vue";
import CourseItem from "../../Components/New/Courses/CourseItem.vue";
import { default as _chunk } from 'lodash.chunk'
import {computed, ref} from "vue";
import PathItem from "../../Components/New/Courses/PathItem.vue";
import ArrowSvg from '@svg/new-arrow.svg'
import ChevronSvg from '@svg/new-chevron.svg'
import {Tab, TabGroup, TabList, TabPanel, TabPanels} from "@headlessui/vue";
import CourseCarousel from "../../Components/New/CourseCarousel.vue";

const props = defineProps({
    activity: [null, Array],
    latest: Array,
    featured: Array,
    trending: Array,
    testimonials: Array,
    membership: Object,
    paths: Array,
    coupon: String,
    parityCoupon: {},
    topics: Array,
})

const chunkedTestimonials = computed(() => _chunk(props.testimonials, 2))

const carouselLatest = ref(null)
</script>

<template>
    <Head>
        <meta head-key="description" name="description" content="Practical screencasts for developers. Learn Laravel, Livewire, Inertia, Vue and more.">
        <meta name="twitter:title" content="Codecourse">
        <meta name="twitter:description" content="Practical screencasts for developers. Learn Laravel, Livewire, Inertia, Vue and more.">
        <meta name="twitter:card" content="summary">
        <meta name="twitter:widgets:new-embed-design" content="on">
        <meta property="og:image" :content="`/images/logo-social-card.png`">
        <meta property="twitter:image:src" :content="`/images/logo-social-card.png`">
    </Head>

    <div class="bg-[rgba(9,13,37,1)] relative z-[1] antialiased bg-[url('/images/home-background.svg')] bg-no-repeat bg-[center_top_-240px] md:bg-[center_top_-80px] overflow-x-hidden">
        <div class="max-w-7xl mx-auto px-6">
            <Navigation />
            <!--Header-->
            <section class="mt-6 md:mt-16">
                <hgroup class="text-center text-balance w-full md:w-8/12 mx-auto md:mt-40">
                    <h1 class="text-[2.85rem] md:text-[4rem] leading-[1] font-bold bg-gradient-to-b from-[rgba(244,244,255,1)] to-[rgba(181,180,207,1)] text-transparent bg-clip-text py-2">Get ready to build something awesome.</h1>
                    <h2 class="text-lg md:text-xl font-semibold bg-gradient-to-b to-[rgba(169,198,249,1)] from-[rgba(117,137,172,1)] text-transparent bg-clip-text mt-5">We create the most practical screencasts for developers</h2>
                </hgroup>

                <nav class="mx-auto mt-12 md:mt-16 flex flex-wrap items-center justify-center md:space-x-8 md:h-[35px]">
                    <template
                        class="w-1/2 md:w-auto flex justify-center md:block"
                        v-for="link in [
                            { label: 'Courses', href: '/library', show: true },
                            { label: 'Topics', href: '/topics', show: true },
                            { label: 'Paths', href: '/paths', show: true, },
                            { label: 'Search', href: '/search', preserveScroll: true, show: true },
                            { label: 'Sign up', href: '/auth/register', show: !$page.props.auth.user },
                            { label: 'Premium', href: '/pro', show: $page.props.auth.user && !$page.props.auth.user.subscription.active },
                        ]"
                        :key="link.href"
                    >
                        <div class="w-1/2 md:w-auto flex justify-center md:block" v-if="link.show">
                            <Link :href="link.href" :preserve-scroll="link?.preserveScroll ?? false" class="py-3 px-2 bg-gradient-to-b from-[rgba(132,156,241,1)] to-[rgba(76,90,139,1)] text-transparent bg-clip-text font-semibold flex items-center space-x-2.5 group">
                                <div class="bg-[rgba(89,89,236,0.15)] group-hover:bg-[rgba(89,89,236,0.20)] transition-all duration-200 size-[30px] rounded-full flex items-center justify-center">
                                    <ArrowSvg class="size-[1.15rem] text-[rgba(126,126,255,1)] rotate-[140deg] group-hover:rotate-180 transition-all duration-200" />
                                </div>
                                <span>{{ link.label }}</span>
                            </Link>
                        </div>
                    </template>
                </nav>

                <!--Pick up where you left off-->
                <div class="mt-28 md:mt-48 bg-[rgba(20,22,57,1)] rounded-2xl px-7 py-8 md:p-10 relative after:absolute after:bg-contain after:bg-[url('/images/purple-circle.svg')] after:-left-[300px] after:-top-[300px] after:w-[700px] after:h-[700px] after:z-[1] after:pointer-events-none overflow-hidden" v-if="$page.props.auth.user && activity.length">
                    <hgroup class="md:bg-[url('/images/pause.svg')] bg-no-repeat md:pl-22 bg-contain">
                        <h2 class="text-3xl text-[rgba(233,232,255,1)] font-bold leading-none">{{ $page.props.auth.user.first_name }}</h2>
                        <h3 class="text-lg text-[rgba(151,162,206,1)] font-semibold mt-5 leading-none">Pick up where you left off</h3>
                    </hgroup>
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
                        <CourseItem topicClasses="bg-[rgba(41,43,95,1)]" metaClasses="bg-[rgba(41,43,95,1)]" class="h-auto bg-[rgba(27,32,74,1)]" v-for="course in activity" :key="course.id" :course="course">
                            <template v-slot:progress>
                                <CourseProgress
                                    class="mt-4"
                                    :totalSeconds="course.duration_seconds"
                                    :percentageProgress="course.user.percentage_progress"
                                    :playedSeconds="course.user.played_seconds"
                                    :remainingSeconds="course.user.remaining_seconds"
                                />
                            </template>
                        </CourseItem>
                    </div>
                </div>
                <!--/Pick up where you left off-->

                <!--Arrows-->
                <div class="mt-28 flex items-center flex-col justify-center">
                    <ChevronSvg class="size-10 text-[rgba(148,162,209,0.5)]" />
                    <ChevronSvg class="size-10 -mt-6 text-[rgba(112,120,176,1)]" />
                </div>
                <!--/Arrows-->
            </section>
            <!--/Header-->

            <div class="space-y-28 md:space-y-48 mt-32">
                <!--Latest/featured/trending-->
                <div class="relative after:absolute after:bg-contain after:bg-[url('/images/blue-circle.svg')] after:-left-[250px] after:-top-[250px] after:w-[700px] after:h-[700px] after:z-[1] after:pointer-events-none">
                    <TabGroup>
                        <TabList class="space-x-6 sm:space-x-12 lg:space-x-40 flex items-center justify-between sm:justify-start">
                            <Tab
                                class="text-left ui-focus-visible:ring-0 focus:outline-none group"
                                v-slot="{ selected }"
                                v-for="tab in [
                                    { title: 'Latest', subtitle: 'Fresh from the library' },
                                    { title: 'Featured', subtitle: 'Courses we think you\'ll love' },
                                    { title: 'Trending', subtitle: 'Popular right now' },
                                ]"
                            >
                                <div class="relative before:absolute mb-5 before:transition-colors before:bg-transparent before:h-[5px] before:-bottom-5 before:rounded-full before:left-0 before:w-22 group-hover:before:bg-[rgba(67,90,210,0.5)] opacity-50 transition-opacity group-hover:opacity-100" :class="{ 'before:!bg-[rgba(67,90,210,1)] !opacity-100': selected }">
                                    <h2 class="text-xl md:text-2xl font-bold text-[rgba(233,232,255,1)] leading-none">
                                        {{ tab.title }}
                                    </h2>
                                    <p class="text-[rgba(148,162,209,1)] font-semibold mt-1.5 hidden md:block">
                                        {{ tab.subtitle }}
                                    </p>
                                </div>
                            </Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel class="mt-12 relative">
                                <CourseCarousel :courses="latest" />
                            </TabPanel>
                            <TabPanel class="mt-12 relative">
                                <CourseCarousel :courses="featured" />
                            </TabPanel>
                            <TabPanel class="mt-12 relative">
                                <CourseCarousel :courses="trending" />
                            </TabPanel>
                        </TabPanels>
                    </TabGroup>
                </div>
                <!--/Latest/featured/trending-->

                <!--Text section-->
                <div class="flex flex-col items-center relative after:absolute after:z-[-1] after:w-full after:h-full after:bg-[url('/images/planet-glow.webp')] after:bg-cover after:bg-[center_bottom_-4rem] after:bg-no-repeat pb-40 h-[504px] -mx-6 md:mx-auto">
                    <hgroup class="mx-auto text-center w-8/12">
                        <h2 class="text-[rgba(233,232,255,1)] text-3xl md:text-4xl font-bold">Actually useful screencasts</h2>
                        <h3 class="text-[rgba(148,162,209,1)] text-lg md:text-[1.2rem] font-semibold mt-4">Skip the theory. In every course, you'll build something useful and leave with practical knowledge you can use anywhere.</h3>
                    </hgroup>

                    <Link :href="`/library`" class="shrink-0 mt-14 bg-[rgba(25,140,255,1)] inline-flex items-center justify-center rounded-full font-semibold text-[rgba(235,248,255,1)] px-20 h-12 md:h-14 leading-none hover:-translate-y-[1px] hover:drop-shadow-[2px_3px_5px_rgba(25,140,255,0.5)] transition-all text-sm lg:text-base">
                        Browse the library
                    </Link>
                </div>
                <!--/Text section-->

                <!--Paths-->
                <div>
                    <div class="flex flex-wrap md:flex-nowrap items-center justify-center md:space-x-16 bg-[url('/images/paths-background.svg')] bg-no-repeat pb-16 bg-[left_top_12rem] relative after:absolute after:bg-contain after:bg-[url('/images/blue-circle.svg')] after:-left-[300px] after:-top-[300px] after:w-[900px] after:h-[900px] after:z-[1] after:pointer-events-none">
                        <div class="shrink-0 md:-mt-20 bg-cover text-center md:text-left">
                            <hgroup>
                                <h2 class="text-[rgba(233,232,255,1)] text-3xl md:text-4xl font-bold">Got a goal in mind?</h2>
                                <h3 class="text-[rgba(148,162,209,1)] text-lg md:text-[1.2rem] font-semibold mt-4">Master a topic with paths</h3>
                            </hgroup>
                        </div>
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12 md:mt-0">
                            <PathItem v-for="path in paths" :path="path" :show-type="true" />
                        </div>
                    </div>

                    <div class="flex items-center justify-center">
                        <Link :href="`/paths`" class="bg-[rgba(25,140,255,1)] inline-flex items-center justify-center rounded-full font-semibold text-[rgba(235,248,255,1)] px-20 h-12 md:h-14 leading-none hover:-translate-y-[1px] hover:drop-shadow-[2px_3px_5px_rgba(25,140,255,0.5)] transition-all text-sm lg:text-base">
                            Explore all paths
                        </Link>
                    </div>
                </div>
                <!--/Paths-->

                <!--Testimonials-->
                <div class="!mt-60 z-[-1] relative before:absolute before:-top-96 before:w-full before:h-full before:bg-[url('/images/grid-background.svg')] before:bg-no-repeat before:bg-[center_top_-8rem] after:absolute after:-top-96 after:bg-gradient-to-b after:from-[rgba(9,13,37,1)] after:h-48 after:w-full -mx-6 md:-mx-24">
                    <hgroup class="mx-auto text-center w-8/12">
                        <h2 class="text-[rgba(233,232,255,1)] text-3xl md:text-4xl font-bold">You're in great company</h2>
                        <h3 class="text-[rgba(148,162,209,1)] text-lg md:text-[1.2rem] font-semibold mt-4">Thousands of developers improve their skills every day with Codecourse</h3>
                    </hgroup>

                    <div class="mt-12 md:mt-24 overflow-x-hidden flex items-center relative before:absolute before:left-0 before:h-full before:w-28 before:z-20 before:bg-gradient-to-r before:from-[rgba(8,13,39,1)] after:absolute after:right-0 after:h-full after:w-28 after:z-20 after:bg-gradient-to-r after:from-transparent after:to-[rgba(8,13,39,1)]">
                        <div class="flex space-x-6" :class="classes" v-for="classes in ['animate-marquee-slow', 'animate-marquee-repeat-slow absolute top-0 pl-6']">
                            <div class="space-y-6 w-64 md:min-w-96" v-for="(testimonials, index) in chunkedTestimonials" :key="index">
                                <div class="bg-[rgba(20,22,57,1)] p-8 rounded-xl" v-for="testimonial in testimonials" :key="testimonial.id">
                                    <div class="text-[rgba(233,232,255,1)] text-sm leading-relaxed">
                                        {{ testimonial.body }}
                                    </div>
                                    <div class="flex mt-6 space-x-4">
                                        <img :src="testimonial.profile_photo_url" class="size-10 rounded-full" loading="lazy">
                                        <div>
                                            <div class="text-[rgba(255,255,255,1)] font-bold">{{ testimonial.name }}</div>
                                            <div class="text-[rgba(112,120,176,1)] text-xs">
                                                <template v-if="testimonial.twitter_handle">
                                                    <a target="_blank" :href="`https://x.com/${testimonial.twitter_handle}`">@{{ testimonial.twitter_handle }}</a>
                                                </template>
                                                <template v-else-if="testimonial.website_url">
                                                    <a target="_blank" :href="testimonial.website_url">{{ testimonial.website_name ?? testimonial.website_name }}</a>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!--/Testimonials-->

                <!--Topics-->
                <div>
                    <hgroup class="mx-auto text-center md:w-8/12">
                        <h2 class="text-[rgba(233,232,255,1)] text-3xl md:text-4xl font-bold">Start your journey</h2>
                        <h3 class="text-[rgba(148,162,209,1)] text-lg md:text-[1.2rem] font-semibold mt-4">Choose a topic</h3>
                    </hgroup>

                    <div class="flex items-center flex-wrap sm:-mx-4 mt-12 justify-center">
                        <Link :href="`/library/${topic.slug}`" class="block w-full sm:w-auto bg-[rgba(20,22,57,1)] py-8 px-10 rounded-3xl mb-4 sm:m-4 min-w-64" v-for="topic in topics" :key="topic.id">
                            <div class="after:absolute after:h-[1.5px] after:w-full after:bottom-0 after:left-0 after:bg-gradient-to-r after:from-[rgba(53,67,116,0)] after:via-[rgba(53,67,116,1)] after:to-[rgba(53,67,116,0)] relative w-full flex items-center justify-center pb-6 space-x-4">
                                <div class="size-[65px] bg-[rgba(34,36,81,1)] rounded-xl flex items-center justify-center">
                                    <TopicIcon class="size-10" :slug="topic.slug" />
                                </div>
                                <h2 class="font-bold text-[rgba(255,255,255,1)] text-2xl leading-none">{{ topic.title }}</h2>
                            </div>

                            <div class="flex flex-col items-center justify-center text-center mt-4 space-y-1">
                                <div class="text-[rgba(112,120,176,1)] font-medium text-sm">
                                    <span class="text-[rgba(233,232,255,1)] font-medium text-sm">{{ topic.courses_count }}</span> courses
                                </div>
                                <div class="text-[rgba(112,120,176,1)] font-medium text-sm">
                                    <span class="text-[rgba(233,232,255,1)] font-medium">{{ topic.parts_count }}</span> episodes
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
                <!--/Topics-->
            </div>
        </div>
        <Footer />
        <ClientOnly>
            <Modal />
            <Notifications />
        </ClientOnly>
    </div>
</template>
